/**
 * FamilyScreen — one question at a time.
 *
 * Sub-cards:
 *  0  children stance         (picker — gate field)
 *  1  requiredPartnerFaith    (scale 10–100 — gate field)
 *  2  acceptsPastSubstanceUse (yes/no — gate field, last)
 */
import { useState } from "react";
import { Button } from "../components/Button";
import { QuestionCard } from "../components/QuestionCard";
import type { ChildrenStance, FaithPercent } from "../domain/answers";
import { useFlow } from "../flow/FlowContext";
import { useT } from "../i18n";

const CHILDREN_IDS: Exclude<ChildrenStance, null>[] = [
  "wants",
  "does_not_want",
  "open",
  "has_and_living_with",
];

function childrenMsgKey(id: Exclude<ChildrenStance, null>): string {
  if (id === "does_not_want") return "doesNotWant";
  if (id === "has_and_living_with") return "hasLivingWith";
  return id;
}

const FAITH_STEPS: FaithPercent[] = [10, 20, 30, 40, 50, 60, 70, 80, 90, 100];
const TOTAL = 3;

export function FamilyScreen() {
  const { profileAnswers, setProfileAnswers, next, canContinue } = useFlow();
  const t = useT();

  const [card, setCard] = useState(0);
  const pos = t("questionnaire.progressLabel", { current: String(card + 1), total: String(TOTAL) });
  const goNext = () => setCard((i) => Math.min(i + 1, TOTAL - 1));
  const goBack = () => setCard((i) => Math.max(i - 1, 0));

  /* ── Sub-card 0: Children stance ── */
  if (card === 0) {
    return (
      <div className="screen-enter" style={{ flex: 1 }}>
        <QuestionCard question={t("family.children")} position={pos}>
          <div className="q-options">
            {CHILDREN_IDS.map((id) => {
              const key = childrenMsgKey(id);
              return (
                <button
                  key={id}
                  type="button"
                  className={`q-option${profileAnswers.children === id ? " is-selected" : ""}`}
                  onClick={() => {
                    setProfileAnswers((p) => ({ ...p, children: id }));
                    goNext();
                  }}
                  aria-pressed={profileAnswers.children === id}
                >
                  <strong>{t(`family.${key}.label`)}</strong>
                  <span>{t(`family.${key}.hint`)}</span>
                </button>
              );
            })}
          </div>
        </QuestionCard>
      </div>
    );
  }

  /* ── Sub-card 1: Minimum partner faith ── */
  if (card === 1) {
    return (
      <div className="screen-enter" style={{ flex: 1 }}>
        <QuestionCard
          question={t("family.requiredFaith")}
          subtext={t("family.requiredFaithHint")}
          position={pos}
        >
          <div className="q-scale">
            {FAITH_STEPS.map((n) => (
              <button
                key={n}
                type="button"
                className={`q-scale__btn${profileAnswers.requiredPartnerFaith === n ? " is-selected" : ""}`}
                onClick={() => {
                  setProfileAnswers((p) => ({ ...p, requiredPartnerFaith: n as FaithPercent }));
                  goNext();
                }}
                aria-pressed={profileAnswers.requiredPartnerFaith === n}
              >
                {n}%
              </button>
            ))}
          </div>
          <button type="button" className="q-subnav__back" style={{ marginTop: 16 }} onClick={goBack}>
            ← {t("shell.back")}
          </button>
        </QuestionCard>
      </div>
    );
  }

  /* ── Sub-card 2 (last): Accepts past substance use ── */
  return (
    <div className="screen-enter" style={{ flex: 1 }}>
      <QuestionCard question={t("family.acceptsSubstances")} position={pos}>
        <div className="q-options">
          <button
            type="button"
            className={`q-option${profileAnswers.acceptsPastSubstanceUse === true ? " is-selected" : ""}`}
            onClick={() => setProfileAnswers((p) => ({ ...p, acceptsPastSubstanceUse: true }))}
            aria-pressed={profileAnswers.acceptsPastSubstanceUse === true}
          >
            <strong>{t("common.yes")}</strong>
          </button>
          <button
            type="button"
            className={`q-option${profileAnswers.acceptsPastSubstanceUse === false ? " is-selected" : ""}`}
            onClick={() => setProfileAnswers((p) => ({ ...p, acceptsPastSubstanceUse: false }))}
            aria-pressed={profileAnswers.acceptsPastSubstanceUse === false}
          >
            <strong>{t("common.no")}</strong>
          </button>
        </div>
        <button type="button" className="q-subnav__back" style={{ marginTop: 16 }} onClick={goBack}>
          ← {t("shell.back")}
        </button>
      </QuestionCard>
      <div className="shell__footer" style={{ paddingLeft: 0, paddingRight: 0 }}>
        <Button onClick={next} disabled={!canContinue}>
          {t("common.continue")}
        </Button>
      </div>
    </div>
  );
}
